import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  PinDrop as PinDropIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../config/axios";
import { Helmet } from "react-helmet-async";

const PinCodeHistory = () => {
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [courierByTrackingId, setCourierByTrackingId] = useState({});
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!pincode || pincode.length !== 6) return;

    setLoading(true);
    try {
      const response = await api.get(`/orders_by_pincode.php?pincode=${pincode}`);
      if (response.data.success) {
        const historyData = response.data.data;
        setHistory(historyData);

        // Fetch courier info for each history item with a tracking ID
        historyData.forEach(async (h) => {
          if (h.tracking_id && !courierByTrackingId[h.tracking_id]) {
            try {
              const res = await api.get(`/shiprocket_tracking.php?action=track&awb=${h.tracking_id}`);
              if (res.data.success && res.data.data.tracking_data?.shipment_track?.length > 0) {
                const courierName = res.data.data.tracking_data.shipment_track[0].courier_name;
                setCourierByTrackingId(prev => ({ ...prev, [h.tracking_id]: courierName }));
              }
            } catch (e) {
              console.error("Error fetching tracking for history item:", e);
            }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching pincode history:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "dispatched" || s === "ready-to-ship") return { background: "#B76E00", color: "#FFFFFF" };
    if (s === "delivered" || s === "accepted") return { background: "#1E7E34", color: "#FFFFFF" };
    if (s === "pending-approval" || s === "in-progress") return { background: "#0056D2", color: "#FFFFFF" };
    if (s === "canceled") return { background: "#C62828", color: "#FFFFFF" };
    return { background: "#616161", color: "#FFFFFF" };
  };

  const formatStatus = (status) => {
    if (!status) return "N/A";
    return status
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <>
      <Helmet>
        <title>Pin Code History - Ishqnama Art</title>
      </Helmet>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1200px", mx: "auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <PinDropIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 900, fontFamily: "'Playfair Display', serif" }}>
              Pin Code History
            </Typography>
          </Box>

          <Card sx={{
            borderRadius: "24px",
            mb: 4,
            boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.05)"
          }}>
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSearch}>
                <Box sx={{
                  maxWidth: '700px',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: 'center'
                }}>
                  <TextField
                    fullWidth
                    label="Enter Pin Code"
                    variant="outlined"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    placeholder="e.g. 560001"
                    inputProps={{ maxLength: 6 }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: '16px',
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    type="submit"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon sx={{ color: 'white' }} />}
                    disabled={loading || pincode.length !== 6}
                    sx={{
                      borderRadius: '16px',
                      py: 2,
                      px: 4,
                      background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
                      fontWeight: 900,
                      color: '#FFFFFF !important',
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: 'rgba(0,0,0,0.12)',
                        color: 'rgba(0,0,0,0.26) !important'
                      }
                    }}
                  >
                    Search History
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : history.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderRadius: '24px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Tracking ID</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Delivery Partner</TableCell>
                    <TableCell sx={{ fontWeight: 800 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => (
                    <TableRow key={row.order_id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } }}>
                      <TableCell sx={{ fontWeight: 700 }}>#{row.order_id}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(row.order_status)}
                          size="small"
                          sx={{ ...getStatusColor(row.order_status), fontWeight: 800, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {row.tracking_id || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.courier_name || courierByTrackingId[row.tracking_id] || "N/A"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => navigate(`/orders/${row.order_id}`)}
                          sx={{ color: 'primary.main', bgcolor: 'rgba(181, 29, 110, 0.05)', '&:hover': { bgcolor: 'rgba(181, 29, 110, 0.1)' } }}
                        >
                          <VisibilityIcon size="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : pincode.length === 6 && !loading ? (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '24px' }}>
              <LocalShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                No delivery history found for this Pin Code
              </Typography>
            </Box>
          ) : null}
        </motion.div>
      </Box>
    </>
  );
};

export default PinCodeHistory;
